<template>
  <div>
    <div class="card-header animated fadeInRightBig d-flex flex-column align-items-center">
      <time-display />

      <div class="card-body">
        <button
          v-if="$store.state.user.remote_check > 0"
          id="check-check"
          class="btn btn-success btn-lg mb-1"
          :disabled="loading"
          @click="cameraOpen"
        >
          Marcar Ponto
        </button>
      </div>
    </div>

    <permission-manager
      :show-permission-modal="showPermissionModal"
      :permission-status="permissionStatus"
      :is-mobile="isMobile"
      @close="closePermissionModal"
      @continue="startCameraWithLocation"
      @permission-granted="handlePermissionGranted"
      @permission-error="handlePermissionError"
    />

    <face-recognition-camera
      v-if="cameraIsStarted"
      ref="faceCamera"
      :location="location"
      :is-mobile="isMobile"
      @camera-closed="handleCameraClosed"
      @picture-captured="handlePictureCaptured"
    />
  </div>
</template>

<script>
import TimeDisplay from '@/components/Check/TimeDisplay.vue'
import PermissionManager from '@/components/Check/PermissionManager.vue'
import FaceRecognitionCamera from '@/components/Check/FaceRecognitionCamera.vue'

export default {
  name: 'CheckCam',
  components: {
    TimeDisplay,
    PermissionManager,
    FaceRecognitionCamera,
  },
  data() {
    return {
      location: null,
      cameraIsStarted: false,
      loading: false,
      showPermissionModal: false,
      permissionStatus: {
        camera: null,
        location: null,
      },
      isMobile: false,
      cameraPermission: null,
      locationPermission: null,
    }
  },
  computed: {
    allPermissionsGranted() {
      return this.permissionStatus.camera === 'granted'
          && this.permissionStatus.location === 'granted'
    },
  },
  async mounted() {
    this.detectMobileDevice()
    await this.requestLocation()
  },
  methods: {
    detectMobileDevice() {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera
      this.isMobile = /android|webos|iphone|ipad|ipod|blackberry|windows phone/i.test(userAgent)
      console.log('Dispositivo detectado:', this.isMobile ? 'Mobile' : 'Desktop')
      return this.isMobile
    },
    async checkPermissionStatusWithPermissionsAPI() {
      if (!navigator.permissions) {
        console.log('API Permissions não suportada.')
        return
      }

      try {
        const cameraPerm = await navigator.permissions.query({ name: 'camera' })
        this.permissionStatus.camera = this.mapPermissionState(cameraPerm.state)
        cameraPerm.onchange = () => {
          this.permissionStatus.camera = this.mapPermissionState(cameraPerm.state)
        }
      } catch (e) {
        console.warn('Erro ao verificar permissão da câmera:', e)
      }

      try {
        const locationPerm = await navigator.permissions.query({ name: 'geolocation' })
        this.permissionStatus.location = this.mapPermissionState(locationPerm.state)
        locationPerm.onchange = () => {
          this.permissionStatus.location = this.mapPermissionState(locationPerm.state)
        }
      } catch (e) {
        console.warn('Erro ao verificar permissão de localização:', e)
      }

      this.cameraPermission = this.permissionStatus.camera === 'granted'
      this.locationPermission = this.permissionStatus.location === 'granted'
    },
    mapPermissionState(state) {
      switch (state) {
        case 'granted':
          return 'granted'
        case 'denied':
          return 'denied'
        case 'prompt':
          return 'prompt'
        default:
          return 'unknown'
      }
    },
    async checkPermissions() {
      try {
        const cameraOk = await this.checkCameraPermission()
        const locationOk = await this.checkLocationPermission()

        this.permissionStatus.camera = this.cameraPermission ? 'granted' : 'denied'
        this.permissionStatus.location = this.locationPermission ? 'granted' : 'denied'

        await this.checkPermissionStatusWithPermissionsAPI()

        if (!cameraOk || !locationOk) {
          this.showPermissionModal = true
          return false
        }
        return true
      } catch (error) {
        console.error('Erro ao verificar permissões:', error)
        return false
      }
    },
    async checkCameraPermission() {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices()
        const hasCamera = devices.some(device => device.kind === 'videoinput')

        if (!hasCamera) {
          this.$message(
            'Atenção',
            'Nenhuma câmera foi encontrada. Por favor, conecte ou ative sua câmera.',
            'warning',
          )
          this.cameraPermission = false
          return false
        }

        const stream = await navigator.mediaDevices.getUserMedia({ video: true })
        stream.getTracks().forEach(track => track.stop())
        this.cameraPermission = true
        return true
      } catch (error) {
        console.error('Erro ao verificar permissão da câmera:', error)
        this.cameraPermission = false
        return false
      }
    },
    async checkLocationPermission() {
      try {
        if (!navigator.geolocation) {
          this.$message(
            'Atenção',
            'Seu navegador não suporta geolocalização. Por favor, use um navegador atualizado.',
            'warning',
          )
          this.locationPermission = false
          return false
        }

        const location = await this.requestLocation()

        if (!location) {
          this.locationPermission = false
          return false
        }

        this.location = location
        this.locationPermission = true
        return true
      } catch (error) {
        console.error('Erro ao verificar permissão de localização:', error)
        this.locationPermission = false
        return false
      }
    },
    requestLocation() {
      return new Promise(resolve => {
        navigator.geolocation.getCurrentPosition(
          position => {
            const locationData = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            }
            resolve(locationData)
          },
          error => {
            console.error('Erro ao obter localização:', error)
            resolve(null)
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          },
        )
      })
    },
    async cameraOpen() {
      try {
        this.loading = true

        const permissionsGranted = await this.checkPermissions()

        if (permissionsGranted) {
          this.startCamera()
        }
      } catch (error) {
        console.error('Erro ao abrir a câmera:', error)
        this.$message('Erro', 'Ocorreu um erro ao tentar abrir a câmera.', 'error')
      } finally {
        this.loading = false
      }
    },
    startCamera() {
      this.showPermissionModal = false
      this.cameraIsStarted = true

      this.removeAnyMirrorCSS()

      this.$nextTick(() => {
        if (this.$refs.faceCamera) {
          this.$refs.faceCamera.startCamera()
          this.ensureNoMirroring()
        }
      })
    },
    removeAnyMirrorCSS() {
      const existingStyle = document.getElementById('mirror-style')
      if (existingStyle) {
        existingStyle.remove()
      }

      const style = document.createElement('style')
      style.id = 'no-mirror-style'
      style.innerHTML = `
        video, canvas {
          transform: none !important;
          -webkit-transform: none !important;
          -moz-transform: none !important;
          -ms-transform: none !important;
        }
      `
      document.head.appendChild(style)
    },
    ensureNoMirroring() {
      const delays = [100, 500, 1000, 2000]

      delays.forEach(delay => {
        setTimeout(() => {
          const videos = document.querySelectorAll('video')
          videos.forEach(video => {
            video.style.setProperty('transform', 'none', 'important')
            video.style.setProperty('-webkit-transform', 'none', 'important')
          })

          const canvases = document.querySelectorAll('canvas')
          canvases.forEach(canvas => {
            canvas.style.setProperty('transform', 'none', 'important')
            canvas.style.setProperty('-webkit-transform', 'none', 'important')
          })
        }, delay)
      })
    },
    startCameraWithLocation(location) {
      if (location) {
        this.location = location
      }
      this.startCamera()
    },
    closePermissionModal() {
      this.showPermissionModal = false
    },
    handlePermissionGranted(type, data) {
      this.permissionStatus[type] = 'granted'
      if (type === 'camera') {
        this.cameraPermission = true
      } else if (type === 'location') {
        this.locationPermission = true
        if (data) {
          this.location = data
        }
      }
    },
    handlePermissionError(type, error) {
      this.permissionStatus[type] = 'denied'
      if (type === 'camera') {
        this.cameraPermission = false
      } else if (type === 'location') {
        this.locationPermission = false
      }

      console.error(`Erro na permissão ${type}:`, error)
    },
    handleCameraClosed() {
      this.cameraIsStarted = false

      const noMirrorStyle = document.getElementById('no-mirror-style')
      if (noMirrorStyle) {
        noMirrorStyle.remove()
      }
    },
    async handlePictureCaptured(picture, location) {
      try {
        console.log('Rosto detectado na imagem, enviando ao servidor...')
        const api = `${this.$store.state.api}checks/remoteCheck`

        const response = await axios.post(api, {
          picture,
          location: location || this.location,
        })

        if (response.data && response.data.warning) {
          swal('Atenção!', response.data.warning, 'warning')
        } else {
          this.$message('Sucesso', 'Ponto registrado com sucesso!', 'success')
        }

        this.handleCameraClosed()
      } catch (error) {
        const data = error.response?.data
        if (data?.reason === 'geofence') {
          swal('Atenção!', data.message, 'warning')
        } else {
          const errorMessage = data?.message || 'Erro desconhecido'
          swal('Ops!', errorMessage, 'error')
        }
        this.handleCameraClosed()
      } finally {
        this.$loading && this.$loading(false)
      }
    },
  },
}
</script>

<style scoped>
.btn {
  display: block;
  margin-top: 10px;
  width: 100%;
}

.card-header {
  text-align: center;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin: 10px;
  width: calc(100% - 20px);
}

.card-body {
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
}

video, canvas {
  transform: none !important;
  -webkit-transform: none !important;
}
</style>
